import React from 'react';
import type { BadgeVariant } from '../../constants/uiConstants';

// ── Spinner ────────────────────────────────────────────────────────────────────

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = { sm: 'h-5 w-5', md: 'h-8 w-8', lg: 'h-12 w-12' };

export const Spinner: React.FC<SpinnerProps> = ({ size = 'md', className = '' }) => (
  <div className={`flex justify-center items-center ${className}`}>
    <div className={`animate-spin rounded-full border-b-2 border-primary-600 ${sizeMap[size]}`} />
  </div>
);

// ── EmptyState ─────────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center py-10 text-center ${className}`}>
    <Icon className="w-10 h-10 text-gray-300 mb-3" />
    <p className="text-sm font-medium text-gray-500">{title}</p>
    {description && <p className="text-sm text-gray-400 mt-1 max-w-xs">{description}</p>}
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
      >
        {action.label}
      </button>
    )}
  </div>
);

// ── StatusBadge ────────────────────────────────────────────────────────────────

const variantClasses: Record<BadgeVariant, string> = {
  green:  'bg-green-100 text-green-700',
  blue:   'bg-blue-100 text-blue-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red:    'bg-red-100 text-red-700',
  gray:   'bg-gray-100 text-gray-500',
};

interface StatusBadgeProps {
  label: string;
  variant: BadgeVariant;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ label, variant, className = '' }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${variantClasses[variant]} ${className}`}
  >
    {label.replace(/_/g, ' ')}
  </span>
);
